/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  GitBranch,
  X,
  Network,
  AlertCircle,
  Maximize2,
} from 'lucide-react';
import type {
  TransitGateway,
  TGWNodeData,
  TGWRouteTableNodeData,
  TGWAttachmentNodeData,
} from './types';
import { getTagName } from './types';
import { TGWNode, TGWRouteTableNode, TGWAttachmentNode } from './TGWNodes';
import { TGWRouteTableModal } from './TGWRouteTableModal';

// =============================================================================
// LAYOUT CONFIGURATION
// =============================================================================

const LAYOUT_CONFIG = {
  tgwStartY: 50,
  tgwWidth: 340,
  tgwSpacing: 400,
  
  rtbStartY: 180,
  rtbWidth: 180,
  rtbHeight: 140,
  rtbSpacingX: 220,
  rtbSpacingY: 180,
  rtbRowMax: 4,
  
  attachStartY: 420,
  attachWidth: 160,
  attachHeight: 100,
  attachSpacingX: 200,
  attachSpacingY: 140,
  attachRowMax: 5,
};

// =============================================================================
// NODE TYPE MAPPING
// =============================================================================

const nodeTypes = {
  tgwHub: TGWNode,
  tgwRouteTable: TGWRouteTableNode,
  tgwAttachment: TGWAttachmentNode,
};

// =============================================================================
// GRAPH BUILDER
// =============================================================================

function buildTGWGraph(transitGateway: TransitGateway): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  
  const tgw = transitGateway;
  const tgwId = tgw.id;
  const tgwName = getTagName(tgw.tags) || tgwId;
  
  // Calculate TGW section width based on content
  const rtbCount = tgw.route_tables?.length || 0;
  const attachCount = tgw.attachments?.length || 0;
  const rtbRows = Math.ceil(rtbCount / LAYOUT_CONFIG.rtbRowMax);
  
  const rtbWidth = Math.min(rtbCount, LAYOUT_CONFIG.rtbRowMax) * LAYOUT_CONFIG.rtbSpacingX;
  const attachWidth = Math.min(attachCount, LAYOUT_CONFIG.attachRowMax) * LAYOUT_CONFIG.attachSpacingX;
  const sectionWidth = Math.max(LAYOUT_CONFIG.tgwWidth, rtbWidth, attachWidth) + 100;
  
  // Center everything
  const centerX = sectionWidth / 2;
  
  // TGW Hub Node
  const tgwNodeData: TGWNodeData = {
    id: tgwId,
    name: tgwName,
    asn: tgw.amazon_side_asn,
    region: tgw.region,
    state: tgw.state,
    routeTableCount: rtbCount,
    attachmentCount: attachCount,
    isOwner: tgw.is_owner !== false,
    defaultRouteTableId: tgw.default_route_table_id,
  };
  
  const tgwNodeX = centerX - (LAYOUT_CONFIG.tgwWidth / 2);
  
  nodes.push({
    id: `tgw-${tgwId}`,
    type: 'tgwHub',
    position: { x: tgwNodeX, y: LAYOUT_CONFIG.tgwStartY },
    data: tgwNodeData,
  });
  
  // Route Table Nodes - centered
  const routeTables = tgw.route_tables || [];
  const rtbTotalWidth = Math.min(rtbCount, LAYOUT_CONFIG.rtbRowMax) * LAYOUT_CONFIG.rtbSpacingX;
  const rtbStartX = centerX - (rtbTotalWidth / 2);
  
  routeTables.forEach((rtb, rtbIndex) => {
    const rtbId = rtb.id;
    const rtbName = getTagName(rtb.tags) || rtbId;
    const isDefault = rtbId === tgw.default_route_table_id;
    
    // Find associated attachments
    const associatedAttachments = (tgw.attachments || [])
      .filter(att => att.association?.TransitGatewayRouteTableId === rtbId)
      .map(att => att.id);
    
    const rtbNodeData: TGWRouteTableNodeData = {
      id: rtbId,
      name: rtbName,
      isDefault,
      routeCount: rtb.routes?.length || 0,
      routes: rtb.routes || [],
      associatedAttachments,
      tgwId,
    };
    
    // Position RTBs in a grid, centered
    const row = Math.floor(rtbIndex / LAYOUT_CONFIG.rtbRowMax);
    const col = rtbIndex % LAYOUT_CONFIG.rtbRowMax;
    const rtbX = rtbStartX + (col * LAYOUT_CONFIG.rtbSpacingX);
    const rtbY = LAYOUT_CONFIG.rtbStartY + (row * LAYOUT_CONFIG.rtbSpacingY);
    
    nodes.push({
      id: `rtb-${rtbId}`,
      type: 'tgwRouteTable',
      position: { x: rtbX, y: rtbY },
      data: rtbNodeData,
    });
    
    // Edge from TGW to RTB
    edges.push({
      id: `e-tgw-${tgwId}-rtb-${rtbId}`,
      source: `tgw-${tgwId}`,
      target: `rtb-${rtbId}`,
      type: 'smoothstep',
      style: { stroke: '#a855f7', strokeWidth: 2 },
      animated: isDefault,
    });
  });
  
  // Attachment Nodes - centered
  const attachStartY = LAYOUT_CONFIG.rtbStartY + (rtbRows * LAYOUT_CONFIG.rtbSpacingY) + 60;
  const attachments = tgw.attachments || [];
  const attachTotalWidth = Math.min(attachCount, LAYOUT_CONFIG.attachRowMax) * LAYOUT_CONFIG.attachSpacingX;
  const attachStartX = centerX - (attachTotalWidth / 2);
  
  attachments.forEach((att, attIndex) => {
    const attId = att.id;
    
    let resourceName = att.vpc_name || att.resource_id;
    if (att.resource_type === 'direct-connect-gateway') {
      resourceName = 'DX Gateway';
    } else if (att.resource_type === 'vpn') {
      resourceName = att.vpn_connection_id || 'VPN Connection';
    } else if (att.resource_type === 'peering') {
      resourceName = att.peering_attachment_id || 'TGW Peering';
    } else if (att.resource_type === 'connect') {
      resourceName = 'TGW Connect';
    }
    
    const attNodeData: TGWAttachmentNodeData = {
      id: attId,
      type: att.resource_type,
      resourceId: att.resource_id,
      resourceName,
      state: att.state,
      associatedRouteTableId: att.association?.TransitGatewayRouteTableId,
      vpcCidr: att.vpc_cidr,
      subnetIds: att.subnet_ids,
      vpnConnectionId: att.vpn_connection_id,
      dxGatewayId: att.direct_connect_gateway_id,
      peeringTgwId: att.peering_attachment_id,
      peeringRegion: att.peering_region,
      tgwId,
    };
    
    // Position attachments in a grid, centered
    const row = Math.floor(attIndex / LAYOUT_CONFIG.attachRowMax);
    const col = attIndex % LAYOUT_CONFIG.attachRowMax;
    const attX = attachStartX + (col * LAYOUT_CONFIG.attachSpacingX);
    const attY = attachStartY + (row * LAYOUT_CONFIG.attachSpacingY);
    
    nodes.push({
      id: `att-${attId}`,
      type: 'tgwAttachment',
      position: { x: attX, y: attY },
      data: attNodeData,
    });
    
    // Edge from Attachment to its associated RTB (if any)
    if (att.association?.TransitGatewayRouteTableId) {
      edges.push({
        id: `e-att-${attId}-rtb-${att.association.TransitGatewayRouteTableId}`,
        source: `att-${attId}`,
        target: `rtb-${att.association.TransitGatewayRouteTableId}`,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5 3' },
      });
    } else {
      // If no association, connect directly to TGW
      edges.push({
        id: `e-att-${attId}-tgw-${tgwId}`,
        source: `att-${attId}`,
        target: `tgw-${tgwId}`,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '5 3' },
      });
    }
  });
  
  return { nodes, edges };
}

// =============================================================================
// AGGREGATE TGW DATA FROM DISCOVERY
// =============================================================================

function aggregateTGWData(discoveryData: any, tgwId: string): TransitGateway | null {
  if (!discoveryData) return null;
  
  console.log('[TGW] Aggregating data for TGW:', tgwId);
  console.log('[TGW] Discovery has transit_gateways:', !!discoveryData.transit_gateways);
  console.log('[TGW] Discovery has vpcs:', !!discoveryData.vpcs);
  
  // Find the base TGW info
  const baseTgw = (discoveryData.transit_gateways || []).find((tgw: any) => tgw.id === tgwId);
  
  if (!baseTgw) {
    console.log('[TGW] Base TGW not found in transit_gateways array');
    return null;
  }
  
  console.log('[TGW] Found base TGW:', baseTgw.tags?.Name || baseTgw.id);
  
  // Check if TGW already has embedded route_tables and attachments (from topology generator)
  if (baseTgw.route_tables?.length > 0 || baseTgw.attachments?.length > 0) {
    console.log('[TGW] Using embedded data - RTBs:', baseTgw.route_tables?.length, 'Attachments:', baseTgw.attachments?.length);
    return baseTgw as TransitGateway;
  }
  
  // Aggregate attachments from all VPCs
  const attachments: any[] = [];
  const routeTableIds = new Set<string>();
  
  // Search in vpcs object or array
  const vpcs = discoveryData.vpcs;
  if (vpcs) {
    const vpcList = Array.isArray(vpcs) ? vpcs : Object.values(vpcs);
    console.log('[TGW] Scanning', vpcList.length, 'VPCs for attachments');
    
    for (const vpc of vpcList) {
      const vpcInfo = (vpc as any).info || vpc;
      const vpcAttachments = vpc.transit_gateway_attachments || vpcInfo.transit_gateway_attachments || [];
      
      for (const att of vpcAttachments) {
        const attTgwId = att.transit_gateway_id || att.TransitGatewayId;
        if (attTgwId === tgwId) {
          // Get VPC info for name
          const vpcName = vpcInfo.tags?.Name || vpcInfo.name || vpcInfo.vpc_id || vpcInfo.id || vpc.id;
          const vpcCidr = vpcInfo.cidr_block || vpcInfo.cidr;
          
          console.log('[TGW] Found attachment in VPC:', vpcName, 'Attachment:', att.id);
          
          attachments.push({
            id: att.id || att.TransitGatewayAttachmentId,
            resource_type: att.resource_type || 'vpc',
            resource_id: att.resource_id || vpcInfo.id || vpc.id,
            vpc_name: att.tags?.Name || vpcName,
            vpc_cidr: vpcCidr,
            state: att.state || att.State || 'available',
            association: att.association,
            subnet_ids: att.subnet_ids,
          });
          
          // Track route table IDs
          if (att.association?.TransitGatewayRouteTableId) {
            routeTableIds.add(att.association.TransitGatewayRouteTableId);
          }
        }
      }
    }
  }
  
  // Also check for top-level tgw_attachments or transit_gateway_attachments
  const topLevelAttachments = discoveryData.tgw_attachments || discoveryData.transit_gateway_attachments || [];
  console.log('[TGW] Top-level attachments:', topLevelAttachments.length);
  
  for (const att of topLevelAttachments) {
    const attTgwId = att.transit_gateway_id || att.TransitGatewayId;
    if (attTgwId === tgwId) {
      const existingAtt = attachments.find(a => 
        a.id === att.id || a.id === att.TransitGatewayAttachmentId
      );
      if (!existingAtt) {
        console.log('[TGW] Found top-level attachment:', att.id);
        attachments.push({
          id: att.id || att.TransitGatewayAttachmentId,
          resource_type: att.resource_type || att.ResourceType || 'vpc',
          resource_id: att.resource_id || att.ResourceId,
          vpc_name: att.tags?.Name || att.vpc_name,
          state: att.state || att.State || 'available',
          association: att.association || (att.Association ? {
            TransitGatewayRouteTableId: att.Association.TransitGatewayRouteTableId
          } : undefined),
        });
        
        const rtbId = att.association?.TransitGatewayRouteTableId || 
                      att.Association?.TransitGatewayRouteTableId;
        if (rtbId) routeTableIds.add(rtbId);
      }
    }
  }
  
  console.log('[TGW] Total attachments found:', attachments.length);
  console.log('[TGW] Route table IDs found:', Array.from(routeTableIds));
  
  // Build route tables array from the IDs we found
  const routeTables = Array.from(routeTableIds).map(rtbId => ({
    id: rtbId,
    tags: { Name: rtbId },
    routes: [], // Routes would need to come from a separate API call
  }));
  
  const result = {
    ...baseTgw,
    attachments,
    route_tables: routeTables,
  };
  
  console.log('[TGW] Final aggregated TGW:', result.tags?.Name, 'RTBs:', result.route_tables.length, 'Attachments:', result.attachments.length);
  
  return result;
}

// =============================================================================
// TGW VISUALIZATION MODAL COMPONENT
// =============================================================================

interface TGWVisualizationModalProps {
  discoveryData: any;
  tgwId: string;
  onClose: () => void;
}

export const TGWVisualizationModal: React.FC<TGWVisualizationModalProps> = ({
  discoveryData,
  tgwId,
  onClose,
}) => {
  const [selectedRtb, setSelectedRtb] = useState<TGWRouteTableNodeData | null>(null);
  
  // Find and aggregate the specific TGW data
  const transitGateway: TransitGateway | null = useMemo(() => {
    return aggregateTGWData(discoveryData, tgwId);
  }, [discoveryData, tgwId]);
  
  // Build graph for this TGW
  const { initialNodes, initialEdges } = useMemo(() => {
    if (!transitGateway) {
      return { initialNodes: [], initialEdges: [] };
    }
    const { nodes, edges } = buildTGWGraph(transitGateway);
    return { initialNodes: nodes, initialEdges: edges };
  }, [transitGateway]);
  
  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);
  
  // Handle node click
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    if (node.type === 'tgwRouteTable') {
      setSelectedRtb(node.data as TGWRouteTableNodeData);
    }
  }, []);
  
  if (!transitGateway) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-600">Transit Gateway not found</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    );
  }
  
  const tgwName = getTagName(transitGateway.tags) || transitGateway.id;
  const rtbCount = transitGateway.route_tables?.length || 0;
  const attachCount = transitGateway.attachments?.length || 0;
  const totalRoutes = (transitGateway.route_tables || []).reduce(
    (acc, rtb) => acc + (rtb.routes?.length || 0), 0
  );
  
  return (
    <div className="fixed inset-0 z-[10000] flex flex-col bg-slate-900/80 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
            <GitBranch className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{tgwName}</h2>
            <p className="text-xs text-slate-500 font-mono">{transitGateway.id}</p>
          </div>
          <div className="flex items-center gap-3 ml-6">
            <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
              ASN: {transitGateway.amazon_side_asn?.toLocaleString() || 'N/A'}
            </span>
            <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
              {transitGateway.region || 'N/A'}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              transitGateway.state === 'available' 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              {transitGateway.state}
            </span>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <span>{rtbCount} Route Tables</span>
            <span className="text-slate-300">|</span>
            <span>{attachCount} Attachments</span>
            <span className="text-slate-300">|</span>
            <span>{totalRoutes} Routes</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 relative">
        {initialNodes.length === 0 ? (
          <div className="flex items-center justify-center h-full bg-white">
            <div className="text-center">
              <Network className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No route tables or attachments found</p>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.3}
            maxZoom={1.5}
            nodesDraggable={false}
            nodesConnectable={false}
            defaultEdgeOptions={{
              type: 'smoothstep',
            }}
            className="bg-slate-50"
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#e2e8f0" gap={20} size={1} />
            <Controls className="bg-white shadow-md border-slate-200" />
            <MiniMap
              nodeColor={(node) => {
                if (node.type === 'tgwHub') return '#a855f7';
                if (node.type === 'tgwRouteTable') return '#c084fc';
                return '#94a3b8';
              }}
              className="bg-white shadow-md border-slate-200"
            />
          </ReactFlow>
        )}
        
        {/* Hint overlay */}
        <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg text-xs text-slate-600 border border-slate-200 shadow-sm">
          <AlertCircle className="w-3.5 h-3.5" />
          Click on a Route Table to see detailed routes
        </div>
      </div>
      
      {/* Route Table Modal */}
      {selectedRtb && (
        <TGWRouteTableModal
          data={selectedRtb}
          onClose={() => setSelectedRtb(null)}
        />
      )}
    </div>
  );
};

// =============================================================================
// TGW VISUALIZATION BUTTON (for use in existing TGW modal)
// =============================================================================

interface TGWVisualizationButtonProps {
  onClick: () => void;
}

export const TGWVisualizationButton: React.FC<TGWVisualizationButtonProps> = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium text-sm transition-colors shadow-sm"
    >
      <Maximize2 className="w-4 h-4" />
      View TGW Topology
    </button>
  );
};

export default TGWVisualizationModal;
